import { useEffect } from 'react';
import type { OrganisationDetailsResponse } from 'src/apis/organisation-service/types';
import type { OptionalOrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { handleErrorWithNotification } from 'src/utils/handle-error';

export function useOrganisationDetails({ url }: { url: string }) {
  const organisationDetails = useAxios(
    (axios, baseConfig, url: string) =>
      axios.request<OrganisationDetailsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    organisationDetails.execute(url);
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return organisationDetails;
}

export type OrganisationDetailsRequest = ReturnType<
  typeof useOrganisationDetails
>;

export function useOptionalOrganisationDetails({
  organisationService,
}: {
  organisationService: OptionalOrganisationServiceRequest;
}) {
  const organisationDetails = useAxios(
    (axios, baseConfig, url: string) =>
      axios.request<OrganisationDetailsResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (!isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );

  useEffect(() => {
    if (!organisationService.response) return;

    organisationDetails.execute(
      organisationService.response.data._links.getMyOrganisationDetails.href
    );
    // oxlint-disable-next-line exhaustive-deps
  }, [organisationService.response]);

  if (!organisationService.response) return;

  return organisationDetails;
}

export type OptionalOrganisationDetailsRequest = ReturnType<
  typeof useOptionalOrganisationDetails
>;
